import { useState, useCallback, useMemo } from 'react';
import { ChevronRight, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

const formatCost = (cost) => {
  return `$${Number(cost).toFixed(4)}`;
};

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) {
    return <ArrowUpDown className="ml-1 inline h-3 w-3 text-muted-foreground/50" />;
  }
  return sortDir === 'asc' ? <ArrowUp className="ml-1 inline h-3 w-3" /> : <ArrowDown className="ml-1 inline h-3 w-3" />;
};

const UsageTable = ({ summary }) => {
  const [sortField, setSortField] = useState('total_cost');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());

  const toggleSort = useCallback(
    (field) => {
      if (sortField === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDir('desc');
      }
    },
    [sortField]
  );

  const toggleExpand = useCallback((companyUid) => {
    setExpandedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(companyUid)) {
        next.delete(companyUid);
      } else {
        next.add(companyUid);
      }
      return next;
    });
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    summary.forEach((row) => {
      if (!map[row.company_uid]) {
        map[row.company_uid] = {
          company_uid: row.company_uid,
          company_name: row.company_name,
          total_input_tokens: 0,
          total_output_tokens: 0,
          total_tokens: 0,
          total_cost: 0,
          request_count: 0,
          models: []
        };
      }
      const group = map[row.company_uid];
      group.total_input_tokens += row.total_input_tokens;
      group.total_output_tokens += row.total_output_tokens;
      group.total_tokens += row.total_tokens;
      group.total_cost += Number(row.total_cost);
      group.request_count += row.request_count;
      group.models.push(row);
    });
    return Object.values(map);
  }, [summary]);

  const sorted = useMemo(() => {
    return [...grouped].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'company_name') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (sortField === 'model_count') {
        aVal = a.models.length;
        bVal = b.models.length;
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [grouped, sortField, sortDir]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Usage by Company</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('company_name')}>
                  Company <SortIcon field="company_name" sortField={sortField} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer select-none whitespace-nowrap text-right" onClick={() => toggleSort('model_count')}>
                  Models <SortIcon field="model_count" sortField={sortField} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer select-none whitespace-nowrap text-right" onClick={() => toggleSort('total_input_tokens')}>
                  Input Tokens <SortIcon field="total_input_tokens" sortField={sortField} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer select-none whitespace-nowrap text-right" onClick={() => toggleSort('total_output_tokens')}>
                  Output Tokens <SortIcon field="total_output_tokens" sortField={sortField} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer select-none whitespace-nowrap text-right" onClick={() => toggleSort('total_tokens')}>
                  Total Tokens <SortIcon field="total_tokens" sortField={sortField} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer select-none whitespace-nowrap text-right" onClick={() => toggleSort('total_cost')}>
                  Cost <SortIcon field="total_cost" sortField={sortField} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer select-none whitespace-nowrap text-right" onClick={() => toggleSort('request_count')}>
                  Requests <SortIcon field="request_count" sortField={sortField} sortDir={sortDir} />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((company) => {
                const isExpanded = expandedCompanies.has(company.company_uid);
                return (
                  <>
                    <TableRow key={company.company_uid} className="cursor-pointer hover:bg-muted/50" onClick={() => toggleExpand(company.company_uid)}>
                      <TableCell className="w-8 px-2">
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </TableCell>
                      <TableCell className="font-medium whitespace-nowrap">{company.company_name}</TableCell>
                      <TableCell className="text-right tabular-nums">{company.models.length}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatNumber(company.total_input_tokens)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatNumber(company.total_output_tokens)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatNumber(company.total_tokens)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCost(company.total_cost)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatNumber(company.request_count)}</TableCell>
                    </TableRow>
                    {isExpanded &&
                      company.models.map((model) => (
                        <TableRow key={`${company.company_uid}-${model.model}`} className="bg-muted/30">
                          <TableCell />
                          <TableCell className="pl-8 text-sm text-muted-foreground whitespace-nowrap">{model.model}</TableCell>
                          <TableCell />
                          <TableCell className="text-right tabular-nums text-sm text-muted-foreground">{formatNumber(model.total_input_tokens)}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm text-muted-foreground">{formatNumber(model.total_output_tokens)}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm text-muted-foreground">{formatNumber(model.total_tokens)}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm text-muted-foreground">{formatCost(model.total_cost)}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm text-muted-foreground">{formatNumber(model.request_count)}</TableCell>
                        </TableRow>
                      ))}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default UsageTable;
